// src/hooks/useStockData.ts
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api';
import { StockData } from '../../types/stockDataTypes';
import { applyYahooCloseToStockData } from '../../utils/yahooPrices';
import { useYahooClosePrices } from './useYahooClosePrices';

export const STOCK_DATA_STALE_TIME = 5 * 60 * 1000;
export const STOCK_DATA_GC_TIME = 60 * 60 * 1000;
const STOCK_DATA_REQUEST_TIMEOUT_MS = 20_000;
const STOCK_PREVIEW_REQUEST_TIMEOUT_MS = 8_000;

export interface StockPreviewData {
  ticker: string;
  General?: {
    Name?: string;
    LogoURL?: string;
    WebURL?: string;
    CurrencyCode?: string;
  };
  Summary?: {
    FinancialHealth?: number;
    ValuationScore?: number;
    ProfitabilityScore?: number;
    HistoryScore?: number;
  };
  Highlights?: {
    MarketCapitalization?: number | null;
    RevenueTTM?: number | null;
    PERatio?: number | null;
    PEGRatio?: number | null;
    ForwardPE?: number | null;
    DividendYield?: number | null;
  };
  PriceHistory?: Array<{ date?: string; price?: number }>;
  stockCurrency?: string;
  currency?: string;
}

export const fetchStockData = async (
  ticker: string,
  signal?: AbortSignal
): Promise<StockData> => {
  const response = await api.get(`getStockData/${encodeURIComponent(ticker)}`, {
    signal,
    timeout: STOCK_DATA_REQUEST_TIMEOUT_MS,
  });
  return response.data as StockData;
};

export const fetchStockPreview = async (
  ticker: string,
  signal?: AbortSignal
): Promise<StockPreviewData> => {
  const response = await api.get(
    `getStockPreview/${encodeURIComponent(ticker)}`,
    {
      signal,
      timeout: STOCK_PREVIEW_REQUEST_TIMEOUT_MS,
    }
  );
  return response.data as StockPreviewData;
};

export function useStockPreview(
  ticker?: string,
  placeholderData?: StockPreviewData
) {
  return useQuery<StockPreviewData, Error>({
    queryKey: ['stockPreview', ticker],
    queryFn: ({ signal }) => fetchStockPreview(ticker as string, signal),
    enabled: !!ticker,
    placeholderData,
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useStockData(ticker?: string) {
  const stockQuery = useQuery<StockData, Error>({
    queryKey: ['stockData', ticker],
    queryFn: ({ signal }) => fetchStockData(ticker as string, signal),
    enabled: !!ticker,
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: STOCK_DATA_STALE_TIME,
    gcTime: STOCK_DATA_GC_TIME,
  });

  // Closing prices are independent from the fundamentals payload. Starting
  // both requests together avoids an unnecessary second network waterfall.
  const yahooPricesQuery = useYahooClosePrices([
    stockQuery.data?.ticker || ticker,
  ]);
  const data = useMemo(
    () =>
      stockQuery.data
        ? applyYahooCloseToStockData(stockQuery.data, yahooPricesQuery.data)
        : undefined,
    [stockQuery.data, yahooPricesQuery.data]
  );

  return {
    ...stockQuery,
    data,
  };
}
